import { createSelector } from '@reduxjs/toolkit';

import type { FitmentFields } from 'Core/actions/fitmentSearch/fields';
import { searchRequestSelectionSelector } from 'Core/selectors/search';
import type { ReduxStateType } from 'Core/store';
import { FacetCollection, type FacetValueBase } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import type { VehicleValue } from 'Models/vehicle';
import type { ServerModel, VerifyFitmentProductData } from 'Modules/serverApi/types';

import {
  createFitmentSearchRequestSelectionSelector,
  createVehicleFromUriSelector,
  isVehicleSelectedSelector,
  selectedVehicleSelector,
} from './index';

const emptySelection: FacetValueBase[] = [];

export const fitmentSearchSelector = (state: ReduxStateType): ReduxStateType['fitmentSearch'] =>
  state.fitmentSearch;

const selectedVehicleSelectionSelector = createSelector(
  selectedVehicleSelector,
  (selectedVehicle) => selectedVehicle.selection,
);

export const vehicleSelectionSelector = (state: ReduxStateType): FacetValueBase[] => {
  const vehicleFromUriSelection = createVehicleFromUriSelector(state);
  if (vehicleFromUriSelection.length) {
    return vehicleFromUriSelection;
  }

  if (fitmentSearchConfig.isFitmentSearchWidgetsVisible) {
    if (isVehicleSelectedSelector(state)) {
      return selectedVehicleSelectionSelector(state);
    }

    return createFitmentSearchRequestSelectionSelector()(state);
  }

  return emptySelection;
};

export const isSpecifyDialogOpenedSelector = (state: ReduxStateType): boolean =>
  fitmentSearchSelector(state).isSpecifyDialogOpened;

export const isVehicleSelectionForcedSelector = (state: ReduxStateType): boolean =>
  fitmentSearchSelector(state).isVehicleSelectionForced;

export const showVehicleDialogSelector = (state: ReduxStateType): boolean =>
  fitmentSearchSelector(state).showDialog;

const fieldsSelector = (state: ReduxStateType): FitmentFields => fitmentSearchSelector(state).fields;

export const fitmentSearchBaseFieldsSelector = (state: ReduxStateType): string[] =>
  fieldsSelector(state).base;

export const fitmentSearchExtraFieldsSelector = (state: ReduxStateType): string[] =>
  fieldsSelector(state).extra;

export const fitmentSearchFieldsSelector = createSelector(
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  (base, extra): string[] => [...base, ...extra],
);

export const vehicleWasNotExcludedSelector = createSelector(
  isVehicleSelectedSelector,
  searchRequestSelectionSelector,
  (isVehicleSelected, requestSelection) =>
    isVehicleSelected &&
    !(
      requestSelection.some(
        (v) =>
          (v.field === 'Universal' && v.term === 'True') ||
          (v.field === 'Fitment' && v.term === 'Universal-Fit'),
      ) && requestSelection.some((v) => v.field === 'FilterByVehicle' && v.term === 'False')
    ),
);

export const fitmentTableResponseSelector = (state: ReduxStateType): ServerModel.FitmentsResponse | null =>
  fitmentSearchSelector(state).fitmentTableResponse;

export const boughtTogetherProductIdSelector = (state: ReduxStateType): number | null =>
  fitmentSearchSelector(state).boughtTogether.productId;

const verifyFitmentSelector = (state: ReduxStateType) => fitmentSearchSelector(state).verifyFitment;

export const verifyFitmentProductIdSelector = (state: ReduxStateType): number | null =>
  verifyFitmentSelector(state).productId;

export const verifyFitmentFacetsSelector = (state: ReduxStateType): FacetCollection | null =>
  verifyFitmentSelector(state).response.facets;

export const verifyFitmentProductSelector = (state: ReduxStateType): VerifyFitmentProductData | null =>
  verifyFitmentSelector(state).response.product;

export const verifyFitmentRequestSelectionSelector = (state: ReduxStateType): VehicleValue[] =>
  verifyFitmentSelector(state).request.selection;
